import React from 'react';
import { Check, X } from 'phosphor-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { BookingData } from '@/types/booking.types';
import { resolveBayPresentation } from '@/utils/bayFallbackNotice';
import { formatBookingTimeRange } from '@/utils/timezone-utils';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import { AddToCalendarButton } from '@/components/ui/molecules/AddToCalendarButton';
import { buildBookingCalendarDescription } from '@/utils/calendarExport';
import { triggerNativeUpdateCalendarEvent } from '@/utils/nativeBridge';
import { useVersionSynced } from '@/context/VersionSyncContext';
import { useCalendarSponsorship } from '@/hooks/useCalendarSponsorship';
import './BookingSuccess.css';
import { getCheckInWindowMs } from '@/utils/checkInWindow';

const DAY_FULL: Record<string, string> = {
  SUN: 'Sunday', MON: 'Monday', TUE: 'Tuesday', WED: 'Wednesday',
  THU: 'Thursday', FRI: 'Friday', SAT: 'Saturday',
};

function getFullDay(dateStr: string): string {
  const abbr = (dateStr || '').trim().split(/\s+/)[0].toUpperCase().slice(0, 3);
  return DAY_FULL[abbr] || abbr;
}

function abbrevRange(times: string[]): string {
  if (!times.length) return '';
  const s = times[0].split(' - ')[0].replace(/\s?am$/i, 'a').replace(/\s?pm$/i, 'p');
  const e = times.at(-1)!.split(' - ')[1].replace(/\s?am$/i, 'a').replace(/\s?pm$/i, 'p');
  return `${s}–${e}`;
}

/**
 * When check-in opens — the start time less the facility's check-in
 * window (30 min for a bay, 5 for the short-turnaround rooms).
 *
 * The lead time comes off the created booking, so this and the API's
 * own gate cannot disagree. A fixed 30 minutes here once quoted a
 * time the server refused, producing "Check-in is not available
 * until …" for members who arrived when they were told to.
 */
function checkInFromISO(
  isoString: string,
  timezone: string,
  leadMs: number
): string {
  if (!isoString) return '';
  try {
    const d = new Date(new Date(isoString).getTime() - leadMs);
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
    }).format(d).toLowerCase();
  } catch {
    return '';
  }
}


const BookingSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const bookingData = location.state as BookingData;
  const versionSynced = useVersionSynced();

  const handleClose = () => {
    queryClient.invalidateQueries({ queryKey: ['bookings'] });
    navigate('/home', { replace: true });
  };

  // On reschedule, move any existing calendar event automatically — no user
  // tap required. Bookings are edited in place (same Mongo _id), so this uid
  // matches whatever the original Add to Calendar tap (if any) used; native
  // no-ops if it has no event for this uid. Placed before the early return
  // below so the hook call stays unconditional (Rules of Hooks) — guards
  // internally via optional chaining instead.
  //
  // IMPORTANT: uid must be built from bookingResult._id (Mongo ObjectId) —
  // the SAME identifier SBookingCard.tsx and the cancel flows use — never
  // from bookingResult.bookingId (the separate GHL/CRM calendar event id,
  // also used below for the user-facing "Booking ID" display). Mixing the
  // two meant cancel could never find the event add/reschedule created.
  React.useEffect(() => {
    if (!bookingData?.isRescheduled) return;
    const startTime = bookingData.bookingResult?.startTime;
    const endTime = bookingData.bookingResult?.endTime;
    const calendarUid = bookingData.bookingResult?._id || bookingData.bookingResult?.id;
    if (!startTime || !endTime || !calendarUid) return;
    triggerNativeUpdateCalendarEvent({
      title: bookingData.bookingType || 'Tee Box Booking',
      description: buildBookingCalendarDescription(bookingData.coachName),
      location: [bookingData.location?.name, bookingData.location?.address].filter(Boolean).join(', ') || undefined,
      startTime,
      endTime,
      uid: `${calendarUid}@teebox`,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const successCalendarId = bookingData?.bookingPayload?.calendarId || undefined;
  const successLocationId = bookingData?.bookingPayload?.locationId || undefined;
  const { data: sponsorship } = useCalendarSponsorship(successCalendarId, successLocationId);

  if (!bookingData) {
    return <div className="bs">Loading booking details…</div>;
  }

  const bookingId  = bookingData.bookingResult?.bookingId || bookingData.bookingId;
  // Separate from bookingId (the GHL/CRM calendar id shown above) — this is
  // the Mongo _id used consistently as the calendar bridge's uid everywhere
  // else in the app (SBookingCard.tsx, cancel flows). Falls back to `id` in
  // case a response ever serializes it that way instead — see calendarEvent
  // below, which requires this to be present at all before offering the
  // button, rather than silently sending add_to_calendar with no uid.
  const calendarUid = bookingData.bookingResult?._id || bookingData.bookingResult?.id;
  const timezone   = bookingData.bookingResult?.timezone || 'America/Denver';
  const dayName    = getFullDay(bookingData.date || '');
  const timeAbbrev = abbrevRange(bookingData.times || []);
  const checkIn    = checkInFromISO(
    bookingData.bookingResult?.startTime || '',
    timezone,
    getCheckInWindowMs(bookingData.bookingResult)
  );
  // D-09: read entirely from navigation state — no refetch here.
  const viaEntitlement = !!bookingData?.viaEntitlement;
  const remainingAfter = bookingData?.entitlementRemainingAfter;

  // "Thu · 07" — extract day+num from date string "Thu May 8"
  const dateParts = (bookingData.date || '').replace(/,/g, '').trim().split(/\s+/);
  const dayAbbr   = (dateParts[0] || '').slice(0, 3);
  const dateNum   = dateParts[2] || dateParts[1] || '';
  const whenLine  = `${dayAbbr} · ${dateNum}`;

  const apiTimeRange = bookingData.bookingResult?.startTime && bookingData.bookingResult?.endTime
    ? formatBookingTimeRange(bookingData.bookingResult.startTime, bookingData.bookingResult.endTime, timezone)
    : timeAbbrev;

  // Resolves the BAY/WHERE label, display name, and (auto-assign only) a
  // retrospective fallback note when the assigned bay differs from the bay
  // originally requested. See bayFallbackNotice.ts for the precedence rules.
  const bayPresentation = resolveBayPresentation({
    bayName: bookingData.bayName,
    requestedBayName: bookingData.requestedBayName,
    bookingType: bookingData.bookingType,
    assignedBayName: bookingData.bookingResult?.assignedRooms?.[0]?.name,
  });
  const whereLabel = sponsorship?.isEnabled && sponsorship?.sponsorName
    ? `${bayPresentation.bayName} | ${sponsorship.sponsorName}`
    : bayPresentation.bayName;

  // Requires calendarUid up front (not just startTime/endTime) — without it
  // there's no stable key for native to later find this event by, so a tap
  // would create a calendar entry that cancel/reschedule could never locate
  // and remove. Hiding the button entirely is safer than offering a broken one.
  const calendarEvent = calendarUid && bookingData.bookingResult?.startTime && bookingData.bookingResult?.endTime
    ? {
        title: bookingData.bookingType || 'Tee Box Booking',
        description: buildBookingCalendarDescription(bookingData.coachName),
        location: [bookingData.location?.name, bookingData.location?.address].filter(Boolean).join(', ') || undefined,
        startTime: bookingData.bookingResult.startTime,
        endTime: bookingData.bookingResult.endTime,
        uid: calendarUid ? `${calendarUid}@teebox` : undefined,
      }
    : null;

  return (
    <div className="bs">

      {/* Top bar: theme toggle + close */}
      <div className="bs__top-bar">
        <ThemeToggle />
        <button className="bs__close" onClick={handleClose}>
          <X size={18} color="currentColor" weight="bold" />
        </button>
      </div>

      {/* Pin icon */}
      <motion.div
        className="bs__pin-wrap"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.175, 0.885, 0.32, 1.275] }}
      >
        <div className="bs__pin-circle">
          <Check size={40} weight="bold" color="#0b0b0a" />
        </div>
        <div className="bs__pin-stem" />
      </motion.div>

      {/* Booking ID */}
      <motion.div
        className="bs__booking-id-wrap"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.4 }}
      >
        <span className="bs__booking-id-label">Booking ID</span>
        <span className="bs__booking-id">{bookingId}</span>
      </motion.div>

      {/* Hero text */}
      <motion.div
        className="bs__hero"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.4, ease: 'easeOut' }}
      >
        <h1 className="bs__headline">{bookingData.isRescheduled ? 'Rescheduled.' : "You're in."}</h1>
        <h1 className="bs__headline bs__headline--muted">See you {dayName}.</h1>
      </motion.div>

      {/* Info card */}
      <motion.div
        className="bs__info-card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4, ease: 'easeOut' }}
      >
        <div className="bs__info-row">
          <div className="bs__info-col">
            <span className="bs__info-label">When</span>
            <span className="bs__info-primary">{whenLine}</span>
            <span className="bs__info-sub">{apiTimeRange}</span>
          </div>
          <div className="bs__info-sep" />
          <div className="bs__info-col">
            <span className="bs__info-label">{bayPresentation.fieldLabel}</span>
            <span className="bs__info-primary">{whereLabel}</span>
            <span className="bs__info-sub">{bookingData.location?.name}</span>
          </div>
        </div>
        <div className="bs__info-divider" />
        {viaEntitlement ? (
          <div className="bs__info-row">
            <div className="bs__info-col">
              <span className="bs__info-label">Check-in</span>
              {/* Falls back to the session start rather than "T-30",
                  which quoted a lead time check-in does not have. */}
              <span className="bs__info-primary">{checkIn || 'At start'}</span>
            </div>
            <div className="bs__info-col">
              <span className="bs__info-label">Sessions Remaining</span>
              <span className="bs__info-primary bs__info-primary--entitlement">{remainingAfter}</span>
            </div>
          </div>
        ) : (
          <div className="bs__info-col bs__info-col--checkin">
            <span className="bs__info-label">Check-in</span>
            {/* Falls back to the session start rather than "T-30",
                which quoted a lead time check-in does not have. */}
            <span className="bs__info-primary">{checkIn || 'At start'}</span>
          </div>
        )}
      </motion.div>

      {/* Bay fallback note — auto-assign only, when the assigned bay differs
          from the requested primary bay */}
      {bayPresentation.fallbackMessage && (
        <motion.div
          className="bs__bay-note"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.58, duration: 0.4, ease: 'easeOut' }}
        >
          {bayPresentation.fallbackMessage}
        </motion.div>
      )}

      {/* Sponsor card */}
      {sponsorship?.isEnabled && sponsorship?.sponsorName && (
        <motion.div
          className="bs__sponsor-card"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.62, duration: 0.4, ease: 'easeOut' }}
        >
          <div className="bs__sponsor-header">
            {sponsorship.logoUrl && (
              <img className="bs__sponsor-logo" src={sponsorship.logoUrl} alt={sponsorship.sponsorName} />
            )}
            <div className="bs__sponsor-meta">
              <span className="bs__sponsor-label">PRESENTED BY</span>
              <span className="bs__sponsor-name">{sponsorship.sponsorName}</span>
            </div>
          </div>
          {sponsorship.sponsorMessage && (
            <p className="bs__sponsor-message">{sponsorship.sponsorMessage}</p>
          )}
        </motion.div>
      )}

      {/* CTAs */}
      <motion.div
        className="bs__actions"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.65, duration: 0.4 }}
      >
        {calendarEvent && versionSynced && (
          <AddToCalendarButton event={calendarEvent} variant="text" label="Add to Calendar" className="bs__cta-calendar" />
        )}
        <button className="bs__cta-ghost" onClick={handleClose}>
          Back to home
        </button>
      </motion.div>

    </div>
  );
};

export default BookingSuccess;
